/**
 * MissingDocumentsView.jsx — COA-05
 * ---------------------------------------------------------------
 * Coordinator's cross-case operational queue of document rows.
 * Pure dense data-grid (no cards, no KPI widgets, no charts).
 *
 * Owner brief columns (10):
 *   Patient · Case · Treatment · Required Document · Assigned To
 *   · Status · Last Reminder · Due Date · Age · Actions
 *
 * Owner brief filters (5):
 *   Search · Treatment · Assigned To · Status · Age Bucket
 *
 * Allowed row actions:
 *   Open Case · View Document Requirement · Send Reminder
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Bell, FileText, Search, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import DataGrid from '../../components/journey-ux/DataGrid';
import Pill from '../../components/journey-ux/Pill';
import FilterChips from '../../components/journey-ux/FilterChips';

/* ─── tone tables ─────────────────────────────────────────────── */
const STATUS_TONE = {
  missing:   { tone: 'rose',   label: 'Missing' },
  requested: { tone: 'amber',  label: 'Requested' },
  approved:  { tone: 'emerald',label: 'Approved' },
  uploaded:  { tone: 'sky',    label: 'Uploaded' },
  rejected:  { tone: 'rose',   label: 'Rejected' },
};
const AGE_TONE = {
  fresh:  { tone: 'slate',  label: 'Fresh (<3d)' },
  aging:  { tone: 'amber',  label: 'Aging (3-7d)' },
  stale:  { tone: 'rose',   label: 'Stale (>7d)' },
};
const SOURCE_TONE = {
  template: 'sky', offer: 'amber', campaign: 'violet', override: 'rose',
};

/* ─── helpers ─────────────────────────────────────────────────── */
const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '—';
  }
};
const _short = (id) => (id ? `#${String(id).slice(-6)}` : '—');
const dueLabel = (offsetDays) => {
  if (offsetDays === null || offsetDays === undefined) return '—';
  const n = Number(offsetDays);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '+0d';
  return n > 0 ? `+${n}d` : `${n}d`;
};

/* ─── component ───────────────────────────────────────────────── */
export default function MissingDocumentsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState({ all: 0, missing: 0, requested: 0, approved: 0 });
  const [loading, setLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [sending, setSending] = useState(null); // row.row_id while reminder is in flight
  const [reloadTick, setReloadTick] = useState(0);

  // Filters — exactly 5 per brief
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all'); // all | missing | requested | approved
  const [treatment, setTreatment] = useState('__all__');
  const [assignedTo, setAssignedTo] = useState('__all__');
  const [ageBucket, setAgeBucket] = useState('__all__');
  // COA-05 — content-quality filter (MISSING / INVALID / COMPLETE)
  const [contentFilter, setContentFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, valid] = await Promise.all([
        api.get('/clinic/coordinator/missing-documents'),
        api.get('/clinic/missing-documents/content-validation').catch(() => ({ data: { items: [] } })),
      ]);
      const data = docs?.data;
      // COA-05 — index content-validation verdicts by (case_id, row_id) and
      // merge a synthetic `content_state` field per row so the lens can
      // distinguish MISSING / INVALID / COMPLETE rather than presence only.
      const vIndex = new Map();
      (valid?.data?.items || []).forEach(v => {
        vIndex.set(`${v.case_id}::${v.row_id}`, v);
      });
      const enrich = (it) => {
        const v = vIndex.get(`${it.case_id}::${it.row_id}`);
        let content_state = 'MISSING';
        if (it.status === 'approved') {
          content_state = (v && v.verdict === 'invalid') ? 'INVALID' : 'COMPLETE';
        } else if (it.status === 'requested') {
          content_state = (v && v.verdict === 'invalid') ? 'INVALID' : 'MISSING';
        } else {
          content_state = 'MISSING';
        }
        return { ...it, content_state, content_verdict: v?.verdict, content_reason: v?.reason };
      };
      if (data?.success) {
        const items = (Array.isArray(data.items) ? data.items : []).map(enrich);
        setRows(items);
        // Re-bucket counts now reflect content state
        const cb = { all: items.length, MISSING: 0, INVALID: 0, COMPLETE: 0 };
        items.forEach(i => { cb[i.content_state] = (cb[i.content_state] || 0) + 1; });
        setBuckets({ ...(data.buckets || {}), ...cb });
      } else {
        setRows([]);
      }
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, reloadTick]);

  // Derive picker options from current rows.
  const treatmentOptions = useMemo(() => {
    const set = new Map();
    rows.forEach((r) => {
      if (r.treatment_slug) set.set(r.treatment_slug, r.treatment_name || r.treatment_slug);
    });
    return Array.from(set.entries()).map(([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const assigneeOptions = useMemo(() => {
    const set = new Map();
    rows.forEach((r) => {
      if (r.default_assignee) set.set(r.default_assignee, r.default_assignee);
    });
    return Array.from(set.keys()).sort();
  }, [rows]);

  // Client filtering — keeps the grid feel instant.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== 'all' && r.status !== status) return false;
      if (treatment !== '__all__' && r.treatment_slug !== treatment) return false;
      if (assignedTo !== '__all__') {
        if (assignedTo === '__unassigned__') {
          if (r.default_assignee) return false;
        } else if (r.default_assignee !== assignedTo) {
          return false;
        }
      }
      if (ageBucket !== '__all__' && r.age_bucket !== ageBucket) return false;
      if (contentFilter !== 'all' && r.content_state !== contentFilter) return false;
      if (q) {
        const hay = `${r.patient_name || ''} ${r.document_name || ''} ${r.treatment_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, status, treatment, assignedTo, ageBucket, search, contentFilter]);

  // Status chip filter strip
  const statusChips = useMemo(() => [
    { id: 'all',       label: 'All',       count: buckets.all },
    { id: 'missing',   label: 'Missing',   count: buckets.missing, tone: 'rose' },
    { id: 'requested', label: 'Requested', count: buckets.requested, tone: 'amber' },
    { id: 'approved',  label: 'Approved',  count: buckets.approved, tone: 'emerald' },
  ], [buckets]);

  // COA-05 — content-quality state chips (MISSING / INVALID / COMPLETE)
  const contentChips = useMemo(() => [
    { id: 'all',      label: 'All',      count: buckets.all },
    { id: 'MISSING',  label: 'Missing',  count: buckets.MISSING || 0,  tone: 'rose'   },
    { id: 'INVALID',  label: 'Invalid',  count: buckets.INVALID || 0,  tone: 'amber'  },
    { id: 'COMPLETE', label: 'Complete', count: buckets.COMPLETE || 0, tone: 'emerald' },
  ], [buckets]);

  const setContentVerdict = useCallback(async (row, verdict) => {
    const reason = verdict === 'invalid'
      ? (window.prompt('Reason (e.g., unreadable, unsigned, corrupt, empty)?') || '').trim()
      : '';
    try {
      const { data } = await api.post('/clinic/missing-documents/content-validation', {
        case_id: row.case_id, row_id: row.row_id, verdict, reason,
      });
      if (data?.success) {
        toast.success(verdict === 'invalid' ? `Marked INVALID: ${reason || '—'}` : 'Marked COMPLETE');
        setReloadTick(t => t + 1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Validation failed');
    }
  }, []);

  const sendReminder = useCallback(async (row) => {
    if (!row) return;
    setSending(row.row_id);
    try {
      const { data } = await api.post('/clinic/coordinator/missing-documents/reminder', {
        case_id: row.case_id, row_id: row.row_id,
      });
      if (data?.success) {
        toast.success(`Reminder sent · "${row.document_name}" · count = ${data.reminder?.count ?? '—'}`);
        setReloadTick((t) => t + 1);
      } else {
        toast.error(data?.message || 'Reminder failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Reminder failed');
    } finally {
      setSending(null);
    }
  }, []);

  /* ─── grid columns ──────────────────────────────────────────── */
  const columns = useMemo(() => [
    {
      id: 'patient',
      header: 'Patient',
      width: 'w-40',
      render: (r) => (
        <div className="leading-tight">
          <div className="text-[12px] font-medium text-slate-900 truncate" data-testid="md-cell-patient">{r.patient_name || '—'}</div>
          <div className="text-[10.5px] text-slate-500 font-mono">{r.country || ''}</div>
        </div>
      ),
    },
    {
      id: 'case',
      header: 'Case',
      width: 'w-20',
      render: (r) => (
        <span className="font-mono text-[11px] text-slate-700" data-testid="md-cell-case">{_short(r.case_id)}</span>
      ),
    },
    {
      id: 'treatment',
      header: 'Treatment',
      width: 'w-32',
      render: (r) => (
        <span className="text-[11px] text-slate-700 truncate" data-testid="md-cell-treatment">{r.treatment_name || r.treatment_slug || '—'}</span>
      ),
    },
    {
      id: 'document',
      header: 'Required Document',
      width: 'w-44',
      render: (r) => (
        <div className="leading-tight">
          <div className="text-[12px] text-slate-900 truncate" data-testid="md-cell-doc">{r.document_name}</div>
          {r.source && (
            <div className="mt-0.5">
              <Pill tone={SOURCE_TONE[r.source] || 'slate'} className="text-[9.5px]">{r.source}</Pill>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'assigned',
      header: 'Assigned To',
      width: 'w-32',
      render: (r) => {
        if (!r.default_assignee && !r.default_role) {
          return <span className="text-rose-500 text-[10.5px] uppercase tracking-wider" data-testid="md-cell-assigned">Unassigned</span>;
        }
        return (
          <div className="leading-tight">
            <div className="text-[11px] text-slate-800" data-testid="md-cell-assigned">{r.default_role || '—'}</div>
            {r.default_assignee && (
              <div className="text-[10.5px] text-slate-500 font-mono">{_short(r.default_assignee)}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: 'w-32',
      render: (r) => {
        const t = STATUS_TONE[r.status] || { tone: 'slate', label: r.status || '—' };
        // COA-05 — surface content-quality state alongside presence state.
        const CONTENT_TONE = {
          MISSING:  { tone: 'rose',    label: 'Missing'  },
          INVALID:  { tone: 'amber',   label: 'Invalid'  },
          COMPLETE: { tone: 'emerald', label: 'Complete' },
        };
        const ct = CONTENT_TONE[r.content_state] || null;
        return (
          <div className="flex flex-col gap-0.5">
            <Pill tone={t.tone} data-testid={`md-cell-status-${r.status}`}>{t.label}</Pill>
            {ct && (
              <Pill
                tone={ct.tone}
                data-testid={`md-cell-content-${r.content_state}`}
              >
                {ct.label}{r.content_reason ? ` · ${r.content_reason}` : ''}
              </Pill>
            )}
          </div>
        );
      },
    },
    {
      id: 'reminder',
      header: 'Last Reminder',
      width: 'w-24',
      render: (r) => (
        <div className="leading-tight">
          <div className="text-[11px] text-slate-700" data-testid="md-cell-reminder">{fmtRel(r.last_reminder_at)}</div>
          {r.reminder_count > 0 && (
            <div className="text-[10px] text-slate-400">×{r.reminder_count}</div>
          )}
        </div>
      ),
    },
    {
      id: 'due',
      header: 'Due Date',
      width: 'w-16',
      align: 'right',
      render: (r) => <span className="font-mono text-[11px] text-slate-700">{dueLabel(r.due_offset_days)}</span>,
    },
    {
      id: 'age',
      header: 'Age',
      width: 'w-24',
      render: (r) => {
        const t = AGE_TONE[r.age_bucket] || { tone: 'slate', label: '—' };
        return (
          <div className="leading-tight">
            <Pill tone={t.tone}>{r.age_bucket}</Pill>
            <div className="text-[10px] text-slate-500 mt-0.5">{r.age_days}d</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 'w-60',
      render: (r) => (
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/clinic/cases/${r.case_id}/workflow`); }}
            data-testid="md-action-open-case"
            className="text-[10.5px] px-2 py-0.5 rounded border border-slate-200 hover:border-slate-300 text-slate-700 inline-flex items-center gap-1"
            title={t('provider.missingDocs.aOpenWorkflow')}
          >
            <ExternalLink className="h-3 w-3" /> Open
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setActiveRow(r); }}
            data-testid="md-action-view-req"
            className="text-[10.5px] px-2 py-0.5 rounded border border-slate-200 hover:border-slate-300 text-slate-700 inline-flex items-center gap-1"
            title={t('provider.missingDocs.aViewRequirement')}
          >
            <FileText className="h-3 w-3" /> View
          </button>
          {/* COA-05 — Mark invalid (e.g., unreadable / unsigned / corrupt) */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setContentVerdict(r, 'invalid'); }}
            data-testid="md-action-mark-invalid"
            className="text-[10.5px] px-2 py-0.5 rounded border border-amber-300 hover:bg-amber-50 text-amber-700 inline-flex items-center gap-1"
            title={t('provider.missingDocs.aMarkInvalid')}
          >
            Mark Invalid
          </button>
          {/* COA-05 — Mark complete (manual content sign-off) */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setContentVerdict(r, 'complete'); }}
            data-testid="md-action-mark-complete"
            className="text-[10.5px] px-2 py-0.5 rounded border border-emerald-300 hover:bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"
            title={t('provider.missingDocs.aMarkComplete')}
          >
            Mark Complete
          </button>
          {r.status !== 'approved' && (
            <button
              type="button"
              disabled={sending === r.row_id}
              onClick={(e) => { e.stopPropagation(); sendReminder(r); }}
              data-testid="md-action-send-reminder"
              className="text-[10.5px] px-2 py-0.5 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white inline-flex items-center gap-1"
              title={t('provider.missingDocs.aSendReminder')}
            >
              <Bell className="h-3 w-3" />
              {sending === r.row_id ? 'Sending…' : (r.reminder_count > 0 ? 'Remind again' : 'Remind')}
            </button>
          )}
        </div>
      ),
    },
  ], [navigate, sending, sendReminder, setContentVerdict, t]);

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="missing-docs-workspace">
      {/* FILTER STRIP */}
      <div className="bg-white border-b border-slate-100 sticky top-[88px] z-10" data-testid="missing-docs-filters">
        <div className="px-5 pt-2 flex flex-wrap items-center gap-2">
          <div className="relative">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('provider.missingDocs.searchAttr')}
              data-testid="md-filter-search"
              className="h-7 w-64 pl-7 pr-2 text-[12px] border border-slate-200 rounded bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400"
            />
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          </div>

          <select
            value={treatment}
            onChange={(e) => setTreatment(e.target.value)}
            data-testid="md-filter-treatment"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">All treatments</option>
            {treatmentOptions.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>

          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            data-testid="md-filter-assigned"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">All assignees</option>
            <option value="__unassigned__">Unassigned only</option>
            {assigneeOptions.map((uid) => (
              <option key={uid} value={uid}>{uid.slice(0, 16)}…</option>
            ))}
          </select>

          <select
            value={ageBucket}
            onChange={(e) => setAgeBucket(e.target.value)}
            data-testid="md-filter-age"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">All ages</option>
            <option value="fresh">Fresh (&lt;3d)</option>
            <option value="aging">Aging (3-7d)</option>
            <option value="stale">Stale (&gt;7d)</option>
          </select>

          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            data-testid="md-refresh"
            className="ml-auto h-7 inline-flex items-center gap-1 px-2 text-[11px] border border-slate-200 rounded text-slate-700 hover:border-slate-300"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="px-5 py-2">
          <FilterChips
            chips={statusChips}
            activeId={status}
            onChange={setStatus}
            testIdPrefix="md-status-chip"
          />
        </div>

        {/* COA-05 — content-quality filter strip (MISSING / INVALID / COMPLETE) */}
        <div className="px-5 pb-2" data-testid="md-content-filter-strip">
          <FilterChips
            chips={contentChips}
            activeId={contentFilter}
            onChange={setContentFilter}
            testIdPrefix="md-content-chip"
          />
        </div>
      </div>

      {/* Result strip */}
      <div className="px-5 py-1.5 bg-white border-b border-slate-100 text-[11px] text-slate-500 flex items-center gap-2" data-testid="md-result-strip">
        <span>{filtered.length}</span>
        <span>{t('common.of')}</span>
        <span>{t('provider.missingDocs.docRowCount', { count: rows.length })}</span>
        <span className="text-slate-300">·</span>
        <span><strong>{buckets.missing}</strong> {t('provider.missingDocs.missingBadge')}</span>
        <span className="text-slate-300">·</span>
        <span><strong>{buckets.requested}</strong> {t('provider.missingDocs.requested')}</span>
        <span className="text-slate-300">·</span>
        <span><strong>{buckets.approved}</strong> {t('provider.missingDocs.approved')}</span>
        <span className="text-slate-300">·</span>
        <span className="text-amber-700"><strong>{buckets.INVALID || 0}</strong> {t('provider.missingDocs.invalidContent')}</span>
        <span className="text-slate-300">·</span>
        <span className="text-emerald-700"><strong>{buckets.COMPLETE || 0}</strong> {t('provider.missingDocs.contentComplete')}</span>
      </div>

      {/* GRID + DRAWER */}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="md"
            columns={columns}
            rows={filtered}
            rowKey={(r) => `${r.case_id}::${r.row_id}`}
            activeRowId={activeRow ? `${activeRow.case_id}::${activeRow.row_id}` : null}
            onRowClick={setActiveRow}
            loading={loading}
            loadingMessage="Loading missing documents…"
            emptyMessage={
              <div className="text-center py-8 max-w-md mx-auto">
                <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-1.5" />
                <div className="text-[13px] font-semibold text-slate-700">{t('provider.missingDocs.emptyTitle')}</div>
                <div className="text-[11.5px] text-slate-500">{t('provider.missingDocs.emptyHelp')}</div>
              </div>
            }
            maxHeightClass="h-[calc(100vh-240px)]"
          />
        </div>

        {activeRow && (
          <aside
            className="w-[360px] shrink-0 border-l border-slate-200 bg-white overflow-y-auto"
            data-testid="md-drawer"
          >
            <header className="px-4 py-2 border-b border-slate-200 flex items-start justify-between gap-2 sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.missingDocs.documentRequirement')}</div>
                <div className="text-[13px] font-semibold text-slate-900 leading-tight">{activeRow.document_name}</div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">
                  {activeRow.patient_name} · {activeRow.treatment_name || activeRow.treatment_slug} · {_short(activeRow.case_id)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveRow(null)}
                data-testid="md-drawer-close"
                className="text-[11px] text-slate-500 hover:text-slate-800"
              >
                ✕
              </button>
            </header>
            <div className="p-4 space-y-2 text-[12px]">
              <Field label="Status">
                <Pill tone={(STATUS_TONE[activeRow.status] || { tone: 'slate' }).tone}>
                  {(STATUS_TONE[activeRow.status] || { label: activeRow.status }).label}
                </Pill>
              </Field>
              <Field label="Source">
                <Pill tone={SOURCE_TONE[activeRow.source] || 'slate'}>{activeRow.source || '—'}</Pill>
              </Field>
              <Field label="Assigned Role">{activeRow.default_role || '—'}</Field>
              <Field label="Assigned User">{activeRow.default_assignee ? _short(activeRow.default_assignee) : <span className="text-rose-500 uppercase text-[10px] tracking-wider">Unassigned</span>}</Field>
              <Field label="Due offset">{dueLabel(activeRow.due_offset_days)}</Field>
              <Field label="Age">{activeRow.age_days}d · <span className="text-slate-500">{activeRow.age_bucket}</span></Field>
              <Field label="Last reminder">{fmtRel(activeRow.last_reminder_at)} {activeRow.reminder_count > 0 ? `(×${activeRow.reminder_count})` : ''}</Field>
              {activeRow.satisfied_at && (
                <>
                  <Field label="Approved at">{formatDateTimeLocale(activeRow.satisfied_at)}</Field>
                  <Field label="Approved by">{_short(activeRow.satisfied_by)}</Field>
                </>
              )}
              <div className="pt-2 border-t border-slate-100 flex gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate(`/clinic/cases/${activeRow.case_id}/workflow`)}
                  data-testid="md-drawer-open-case"
                  className="text-[11px] px-2 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded inline-flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" /> Open case
                </button>
                {activeRow.status !== 'approved' && (
                  <button
                    type="button"
                    disabled={sending === activeRow.row_id}
                    onClick={() => sendReminder(activeRow)}
                    data-testid="md-drawer-send-reminder"
                    className="text-[11px] px-2 py-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded inline-flex items-center gap-1"
                  >
                    <Bell className="h-3 w-3" /> {activeRow.reminder_count > 0 ? 'Remind again' : 'Send reminder'}
                  </button>
                )}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">{label}</span>
      <span className="text-right text-[12px] text-slate-900 min-w-0">{children}</span>
    </div>
  );
}
